import { ViewIds } from '@ardoq/api-types';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { EphemeralNotificationKeys } from 'ephemeralNotification/types';
import type { TrackingEvent } from 'tracking/trackPayload';
import { BetaBadgeOptionKey } from '@ardoq/status-ui';
import {
  blockDiagramBase64,
  blocksViewBase64,
  bubbleChartBase64,
  dependencyMapBase64,
  modernizedBlockDiagramBase64,
  relationshipsViewBase64,
  timelineViewBase64,
} from './viewThumbnailsBase64';

type ViewDescription = {
  i18nKey: string;
  defaultValue: string;
};

type OnboardingModalConfig = {
  notificationKey: EphemeralNotificationKeys;
  reopenTrackingEvent?: TrackingEvent;
};

type TakeTourConfig = {
  url: string;
  trackingEvent?: TrackingEvent;
};

type BadgeLabelOverride = {
  i18nKey: string;
  defaultValue: string;
};

type ViewInfo = {
  description: ViewDescription;
  knowledgeBaseLink?: string;
  badgeKnowledgeBaseLink?: string;
  shareFeedbackLink?: string;
  onboardingModal?: OnboardingModalConfig;
  takeTour?: TakeTourConfig;
  badgeOptionsOrder?: BetaBadgeOptionKey[];
  badgeLabelOverrides?: Partial<Record<BetaBadgeOptionKey, BadgeLabelOverride>>;
  /* the base64 encoded image representing the view. */
  image: string;
  isBeta?: boolean;
};
const viewInfo: Partial<Record<ViewIds, ViewInfo>> = {
  [ViewIds.BLOCK_DIAGRAM]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.blockDiagram',
      defaultValue: 'Easily see how your data is structured and connected',
    },
    knowledgeBaseLink: KnowledgeBaseLink.BLOCK_DIAGRAM,
    image: blockDiagramBase64,
  },
  [ViewIds.DEPENDENCY_MAP_2]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.dependencyMap',
      defaultValue:
        'Visualize how different parts of your organization rely on each other',
    },
    knowledgeBaseLink: KnowledgeBaseLink.DEPENDENCY_MAP_2,
    image: dependencyMapBase64,
  },
  [ViewIds.RELATIONSHIPS_3]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.relationships',
      defaultValue: 'Analyze the connections within complex sets of data',
    },
    knowledgeBaseLink: KnowledgeBaseLink.RELATIONSHIPS,
    image: relationshipsViewBase64,
  },
  [ViewIds.TIMELINE]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.timeline',
      defaultValue:
        'Track the phases and milestones of your projects and applications',
    },
    knowledgeBaseLink: KnowledgeBaseLink.TIMELINE,
    image: timelineViewBase64,
  },
  [ViewIds.BLOCKS]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.blocks',
      defaultValue:
        'Get flexibility to re-arrange the layout of components and groups in your data visualization',
    },
    badgeKnowledgeBaseLink: KnowledgeBaseLink.INTERACTIVE_BLOCKS,
    isBeta: true,
    image: blocksViewBase64,
  },
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.modernizedBlockDiagram',
      defaultValue: 'Easily see how your data is structured and connected',
    },
    knowledgeBaseLink:
      KnowledgeBaseLink.MODERNIZED_BLOCK_DIAGRAM_IN_VIEWPOINT_BUILDER,
    image: modernizedBlockDiagramBase64,
  },
  [ViewIds.BUBBLE]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.bubble',
      defaultValue:
        'Visualize the relationships between the numeric field values of multiple components',
    },
    knowledgeBaseLink: KnowledgeBaseLink.BUBBLE_CHART,
    image: bubbleChartBase64,
  },
  [ViewIds.COMPONENT_MATRIX_2]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.componentMatrix',
      defaultValue:
        'Spot patterns and gaps with data categorized by field values and references',
    },
    image: '/img/viewsExample/componentMatrix2.svg',
  },
  [ViewIds.ASSISTANT_DIAGRAM]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.assistantDiagram',
      defaultValue:
        'Chat with the Ardoq AI Assistant about the diagram you want to see',
    },
    image: blockDiagramBase64,
    isBeta: true,
  },
  [ViewIds.ASSISTANT_BLOCKS]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.assistantBlocks',
      defaultValue:
        'Chat with the Ardoq AI Assistant to arrange blocks the way you want',
    },
    image: blockDiagramBase64,
    isBeta: true,
  },
  [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.flexibleBlockDiagram',
      defaultValue: 'Automatically lay out the current view as a block diagram',
    },
    image: blockDiagramBase64,
    isBeta: true,
  },
  [ViewIds.HIERARCHICAL_TREEMAP]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.hierarchicalTreemap',
      defaultValue:
        'Compare the parts of a hierarchy as nested rectangles sized by a field',
    },
    image: '/img/viewsExample/hierarchicalTreemap.svg',
    isBeta: true,
  },
  [ViewIds.HIERARCHICAL_PACKING]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.hierarchicalPacking',
      defaultValue:
        'Compare the parts of a hierarchy as nested circles sized by a field',
    },
    image: '/img/viewsExample/hierarchicalPacking.svg',
    isBeta: true,
  },
  [ViewIds.SUNBURST]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.sunburst',
      defaultValue:
        'Compare the parts of a hierarchy as rings, one ring per level',
    },
    image: '/img/viewsExample/sunburst.svg',
    isBeta: true,
  },
  /* The picker reads this map, not `metaInfoTabs` - so without an entry the card renders an
     `<img>` with no `src`, the generic "Learn about the view" in place of a description, and no
     beta badge, whatever `MetaInfoViewStatus` says three files over. A path rather than base64,
     matching the newer thumbnails. */
  [ViewIds.SURFACE_3D]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.surface3d',
      defaultValue:
        'Read a third numeric field as relief over the plane two others form',
    },
    image: '/img/viewsExample/surface3d.svg',
    isBeta: true,
  },
  /* The other three the same flag ships, for the same reason as the surface above: without an
     entry the card is a broken `<img>`, the generic "Learn about the view", and no beta badge -
     whatever `viewStatus` says in `metaInfoTabs`. One line each of what the chart is for, since
     the card has room for one. */
  [ViewIds.CONTOUR]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.contour',
      defaultValue:
        'Read a third numeric field as a map over the plane two others form',
    },
    image: '/img/viewsExample/contour.svg',
    isBeta: true,
  },
  [ViewIds.SPIDER_2]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.spider2',
      defaultValue: 'Compare components across several numeric fields at once',
    },
    image: '/img/viewsExample/spider2.svg',
    isBeta: true,
  },
  [ViewIds.POLAR]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.polar',
      defaultValue:
        'Plot components by one numeric field around a circle and another outward',
    },
    image: '/img/viewsExample/polar.svg',
    isBeta: true,
  },
  [ViewIds.SCATTER_3D]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.scatter3d',
      defaultValue: 'Place each component in space by three numeric fields',
    },
    image: '/img/viewsExample/scatter3d.svg',
    isBeta: true,
  },
  [ViewIds.BUBBLE_3D]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.bubble3d',
      defaultValue:
        'The 3D scatter, with a fourth numeric field varying mark size',
    },
    image: '/img/viewsExample/bubble3d.svg',
    isBeta: true,
  },
  [ViewIds.NETWORK_3D]: {
    description: {
      i18nKey: 'appLayout:viewDescriptions.network3d',
      defaultValue: 'Components and their references in three dimensions',
    },
    image: '/img/viewsExample/network3d.svg',
    isBeta: true,
  },
};

export { viewInfo };
